"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Heart } from "lucide-react";
import { useFavorites } from "@/lib/use-favorites";
import { Button } from "@/ui/components/ui/button";

interface FavoriteButtonProps {
	slug: string;
	variant?: "icon" | "default";
	className?: string;
}

export function FavoriteButton({ slug, variant = "icon", className }: FavoriteButtonProps) {
	const t = useTranslations("favorites");
	const { has, toggle, mounted } = useFavorites();
	const isFavorite = has(slug);
	const [addedByUser, setAddedByUser] = useState(false);

	if (!mounted) return null;

	return (
		<Button
			variant="ghost"
			size={variant === "icon" ? "icon" : "sm"}
			className={`motion-favorite ${className ?? ""}`}
			onClick={() => {
				// The local store may hydrate an existing favorite. Only a user-initiated
				// add gets the small confirmation pop, never hydration or removal.
				setAddedByUser(!isFavorite);
				toggle(slug);
			}}
			aria-label={isFavorite ? t("remove") : t("add")}
			aria-pressed={isFavorite}
			data-liked={isFavorite}
			data-feedback={isFavorite && addedByUser ? "added" : undefined}
		>
			<span className="motion-favorite-icon" aria-hidden="true">
				<Heart
					className={`motion-favorite-heart h-4 w-4 ${isFavorite ? "fill-destructive text-destructive" : ""}`}
				/>
			</span>
			{variant === "default" && <span className="ml-2">{isFavorite ? t("remove") : t("add")}</span>}
		</Button>
	);
}
